/**
 * Formats a dollar amount as a lump sum string with no trailing zeros for
 * whole numbers — e.g. $200 (not $200.00), $150.50 (when fractional).
 * Mirrors the backend's `format_lump_sum` Ruby helper.
 */
export function formatLumpSumAmount(dollars: number): string {
  return dollars === Math.floor(dollars)
    ? `$${Math.floor(dollars)}`
    : `$${dollars.toFixed(2)}`;
}

type TierOption = {
  label: string;
  payRate: number;
};

/**
 * Builds the display label for a tier option in the rate override dropdown.
 * When lump sum is enabled the total shift value is shown; otherwise the
 * hourly rate is shown.
 *
 * Example outputs:
 *   displayAsLumpSum=true,  shiftDuration=4 → "Tier 1 - $200/shift"
 *   displayAsLumpSum=false                  → "Tier 1 - $50.00/hr"
 */
export function formatTierOptionLabel(
  tier: TierOption,
  displayAsLumpSum: boolean,
  shiftDuration: number
): string {
  if (displayAsLumpSum) {
    return `${tier.label} - ${formatLumpSumAmount(tier.payRate * shiftDuration)}/shift`;
  }
  return `${tier.label} - $${tier.payRate.toFixed(2)}/hr`;
}

type OverrideRateOption = {
  displayName: string;
  incentiveCents: number;
  moneyAmountCents?: number | null;
};

/**
 * Builds the display label for an override-rate option in the rate override
 * dropdown. The format follows the workforce group's `displayAsLumpSum`
 * setting: lump sum when true, hourly when false. When `moneyAmountCents` is
 * populated (money-PCE mode) it is the source of truth for the lump total;
 * otherwise the lump is derived from the demand shift duration.
 *
 * Example outputs:
 *   displayAsLumpSum=false                                           → "Overtime - $25.00/hr"
 *   displayAsLumpSum=true, shiftDuration=4                           → "Overtime - $200/shift"
 *   displayAsLumpSum=true, moneyAmountCents=6000                     → "Premium Pay Lump Sum Tier 2 - $60/shift"
 *   displayAsLumpSum=false, moneyAmountCents set (ignored in hourly) → "Premium Pay Lump Sum Tier 2 - $5.00/hr"
 */
export function formatOverrideRateOptionLabel(
  option: OverrideRateOption,
  displayAsLumpSum: boolean,
  shiftDuration: number
): string {
  const rateInDollars = option.incentiveCents / 100;

  if (!displayAsLumpSum) {
    return `${option.displayName} - $${rateInDollars.toFixed(2)}/hr`;
  }

  const lumpInDollars =
    option.moneyAmountCents !== null && option.moneyAmountCents !== undefined
      ? option.moneyAmountCents / 100
      : rateInDollars * shiftDuration;
  return `${option.displayName} - ${formatLumpSumAmount(lumpInDollars)}/shift`;
}

/**
 * Builds the "Base rate" option shown at the top of the rate-override dropdown.
 * Selecting it reverts an override back to the pre-override computed rate.
 *
 * Example outputs:
 *   displayAsLumpSum=false                     → "Base rate - $0.00/hr"
 *   displayAsLumpSum=true, shiftDuration=4     → "Base rate - $0/shift"
 *   displayAsLumpSum=true, rate=2160c, dur=4   → "Base rate - $86.40/shift"
 */
export function formatBaseRateOptionLabel(
  baseRateCents: number,
  displayAsLumpSum: boolean,
  shiftDuration: number
): string {
  const rateInDollars = baseRateCents / 100;
  if (displayAsLumpSum) {
    return `Base rate - ${formatLumpSumAmount(rateInDollars * shiftDuration)}/shift`;
  }
  return `Base rate - $${rateInDollars.toFixed(2)}/hr`;
}

type CurrentRateRecord = {
  averageRate: number;
  displayAsLumpSum: boolean;
  formattedLumpSumRate: string | null;
  incentiveTierLabel: string | null;
};

/**
 * Builds the label rendered for the currently-selected rate in the override
 * dropdown. The persisted rate (averageRate cents) often does not match any
 * available option — selecting it would otherwise display the raw encoded
 * value (e.g. "800:") — so this label is provided via Select's `labelInValue`
 * to keep the displayed rate human-readable.
 *
 * Example outputs:
 *   displayAsLumpSum=false, tierLabel="Tier 1 AA", rate=800c → "Tier 1 AA - $8.00/hr"
 *   displayAsLumpSum=false, no tier label, rate=2345c        → "$23.45/hr"
 *   displayAsLumpSum=true,  tierLabel="Tier 2", lump="$200"  → "Tier 2 - $200/shift"
 */
export function formatCurrentRateLabel(record: CurrentRateRecord): string {
  const rateText =
    record.displayAsLumpSum && record.formattedLumpSumRate
      ? `${record.formattedLumpSumRate}/shift`
      : `$${(record.averageRate / 100).toFixed(2)}/hr`;
  return record.incentiveTierLabel
    ? `${record.incentiveTierLabel} - ${rateText}`
    : rateText;
}
